import type { GuestQuoteLinkParams } from '@repo/api-contracts';
import { useRef } from 'react';
import { useNavigate } from 'react-router';
import { useInvalidateMyQuotes } from '@/entities/quote';
import { refreshSession } from '@/entities/user';
import { trpc } from '@/shared/api';
import { env, myQuoteDetailPath, orderPlacePath } from '@/shared/config';
import { getErrorMessage, getTrpcErrorCode, pendingGuestQuoteStorage } from '@/shared/lib';
import {
  GUEST_QUOTE_CLAIM_MESSAGES,
  useGuestQuoteClaimNoticeStore,
} from './guest-quote-claim-notice.store';

export interface UseClaimGuestQuoteResult {
  /** 연결을 한 번 시도한다. 결과 처리(이동·안내·연결 대기 지우기)는 이 훅이 맡는다. 진행 중에 다시 부르면 아무것도 하지 않는다. */
  readonly claim: (params: GuestQuoteLinkParams) => Promise<void>;
  readonly isPending: boolean;
}

/**
 * 비회원 견적 연결(quotes.claimGuest, 결정 G10·G11) — 앱 셸 훅과 링크 화면 [주문하기]가 함께 쓴다.
 *
 * - 먼저 세션을 다시 받는다. 액세스 토큰의 계정 상태는 발급 때 값이다. 그래서 방금 인증을 마친 회원은 PENDING 이 남아 있어 activeProcedure 가 거절한다.
 * - 성공: 연결 대기를 지우고 내 견적을 다시 읽게 한 뒤 주문하기 화면으로 간다. 주문 gate 가 닫혀 있으면 내 견적 상세로 간다.
 * - 관리자 계정(FORBIDDEN)·다른 계정이 먼저 연결함(CONFLICT)·찾을 수 없음(NOT_FOUND): 다시 시도해도 같다. 연결 대기를 지우고 고객센터 안내를 띄운다.
 *   연락처는 비교하지 않는다 — 링크를 가진 ACTIVE 고객 계정이면 누구나 연결하고, 먼저 연결한 한 계정만 된다.
 * - 그 밖의 실패(네트워크·일시 오류·세션 재발급 실패): 연결 대기를 남기고 실패만 안내한다. 다음 기회(화면 이동·다시 누름)에 다시 시도한다.
 * StrictMode 의 두 번 실행이나 연타로 진행 중에 다시 부르면 ref 가 막는다.
 */
export const useClaimGuestQuote = (): UseClaimGuestQuoteResult => {
  const navigate = useNavigate();
  const invalidate = useInvalidateMyQuotes();
  const mutation = trpc.quotes.claimGuest.useMutation();
  const showNotice = useGuestQuoteClaimNoticeStore((state) => state.show);
  const dismissNotice = useGuestQuoteClaimNoticeStore((state) => state.dismiss);
  const running = useRef(false);

  const claim = async ({ quoteRequestId, signature }: GuestQuoteLinkParams): Promise<void> => {
    if (running.current) return;
    running.current = true;

    try {
      if (!(await refreshSession())) {
        showNotice({ kind: 'FAILED', message: GUEST_QUOTE_CLAIM_MESSAGES.FAILED });
        return;
      }

      // 링크 두 칸만 싣는다. 연결 대기 기억의 저장 시각(savedAt)은 서버 계약(strict)에 없다.
      const result = await mutation.mutateAsync({ quoteRequestId, signature });
      pendingGuestQuoteStorage.clear();
      dismissNotice();
      await Promise.all([invalidate.list(), invalidate.detail(result.quoteRequestId)]);
      void navigate(
        env.VITE_ORDER_PLACEMENT_ENABLED
          ? orderPlacePath(result.quoteRequestId)
          : myQuoteDetailPath(result.quoteRequestId),
        { replace: true },
      );
    } catch (caught) {
      const code = getTrpcErrorCode(caught);

      if (code === 'FORBIDDEN') {
        pendingGuestQuoteStorage.clear();
        showNotice({
          kind: 'CUSTOMER_ONLY',
          message: getErrorMessage(caught, GUEST_QUOTE_CLAIM_MESSAGES.CUSTOMER_ONLY),
        });
      } else if (code === 'CONFLICT') {
        pendingGuestQuoteStorage.clear();
        showNotice({
          kind: 'ALREADY_CLAIMED',
          message: getErrorMessage(caught, GUEST_QUOTE_CLAIM_MESSAGES.ALREADY_CLAIMED),
        });
      } else if (code === 'NOT_FOUND' || code === 'BAD_REQUEST') {
        pendingGuestQuoteStorage.clear();
        showNotice({ kind: 'NOT_FOUND', message: GUEST_QUOTE_CLAIM_MESSAGES.NOT_FOUND });
      } else {
        showNotice({ kind: 'FAILED', message: GUEST_QUOTE_CLAIM_MESSAGES.FAILED });
      }
    } finally {
      running.current = false;
    }
  };

  return { claim, isPending: mutation.isPending };
};
